import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import RevealOnScroll from "../components/RevealOnScroll";
import Comments from "../components/Comments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric", month: "long", day: "numeric",
        });
    } catch {
        return "";
    }
};

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        setPost(null);
        setError(null);
        axios
            .get(`${API}/blog/${slug}`)
            .then((r) => { if (alive) setPost(r.data); })
            .catch((e) => { if (alive) setError(e?.response?.status === 404 ? "not_found" : "error"); });
        return () => { alive = false; };
    }, [slug]);

    return (
        <main className="bg-ivory text-charcoal" data-testid="blog-post-page">
            <Seo
                title={post ? `${post.title} | Alchemy Insights` : "Alchemy Insights"}
                description={post?.excerpt || "Reflections from Alchemy Practice."}
            />
            <Nav />
            <RevealOnScroll>
                <section
                    data-testid="blog-post-hero"
                    className="pt-44 pb-16 lg:pt-52 lg:pb-20"
                    style={{ background: "#FAFAF8" }}
                >
                    <div className="mx-auto max-w-[860px] px-6 lg:px-12">
                        {error === "not_found" ? (
                            <div className="text-center py-20" data-reveal>
                                <span className="font-serif text-4xl" style={{ color: "#9B7D5A" }}>✦</span>
                                <p
                                    className="mt-6 font-serif italic"
                                    style={{ fontSize: "24px", color: "#2C2825" }}
                                >
                                    This reflection cannot be found.
                                </p>
                                <a
                                    href="/alchemy-insights"
                                    className="mt-10 inline-block gold-link text-[12px] tracking-[0.28em] uppercase font-medium"
                                    style={{ color: "#9B7D5A" }}
                                >
                                    ← Back to Alchemy Insights
                                </a>
                            </div>
                        ) : !post ? (
                            <p className="text-center text-charcoal-soft">Loading…</p>
                        ) : (
                            <div data-reveal className="text-center">
                                <p
                                    className="font-medium uppercase"
                                    style={{
                                        fontFamily: "'Figtree', sans-serif",
                                        fontSize: "10px",
                                        letterSpacing: "0.2em",
                                        color: "#9B7D5A",
                                    }}
                                    data-testid="blog-post-category"
                                >
                                    {post.category || (post.tags && post.tags[0]) || ""}
                                </p>
                                <h1
                                    className="mt-6 font-serif leading-[1.05] text-charcoal text-balance"
                                    style={{ fontSize: "clamp(40px, 6vw, 64px)" }}
                                    data-testid="blog-post-title"
                                >
                                    {post.title}
                                </h1>
                                <div className="mx-auto mt-8" style={{ height: 2, width: 64, background: "#8A9E85" }} />
                                <p
                                    className="mt-6 font-serif italic"
                                    style={{ fontSize: "16px", color: "#8A7968" }}
                                >
                                    By {post.author}
                                </p>
                            </div>
                        )}
                    </div>
                </section>

                {post?.featured_image_url && (
                    <div className="mx-auto max-w-[1100px] px-6 lg:px-12 pb-10" data-reveal>
                        <img
                            src={post.featured_image_url}
                            alt={post.title}
                            className="w-full max-h-[560px] object-cover"
                        />
                    </div>
                )}

                {post && (
                    <article
                        data-testid="blog-post-body"
                        className="pb-28 lg:pb-36"
                        style={{ background: "#FAFAF8" }}
                    >
                        <div
                            className="mx-auto px-6 lg:px-12"
                            style={{ maxWidth: "720px" }}
                            data-reveal
                        >
                            {post.body.split(/\n\s*\n/).map((para, i) => (
                                <p
                                    key={i}
                                    className={i === 0 ? "" : "mt-7"}
                                    style={{
                                        fontFamily: "'Figtree', sans-serif",
                                        fontSize: "16px",
                                        lineHeight: 1.9,
                                        color: "#8A7968",
                                    }}
                                >
                                    {para.trim()}
                                </p>
                            ))}

                            {post.tags && post.tags.length > 0 && (
                                <div
                                    className="mt-16 pt-8 flex flex-wrap gap-3"
                                    style={{ borderTop: "1px solid #E2DDD8" }}
                                    data-testid="blog-post-tags"
                                >
                                    {post.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-[10px] tracking-[0.24em] uppercase px-3 py-1.5"
                                            style={{ color: "#9B7D5A", border: "1px solid #E2DDD8" }}
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="mt-20 text-center">
                                <a
                                    href="/alchemy-insights"
                                    data-testid="blog-post-back-link"
                                    className="gold-link text-[12px] tracking-[0.28em] uppercase font-medium"
                                    style={{ color: "#9B7D5A" }}
                                >
                                    ← Back to Alchemy Insights
                                </a>
                            </div>
                        </div>
                    </article>
                )}
                {post && <Comments postSlug={post.slug} />}
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
